// @flow
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {isEqual} from "lodash";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EnvironmentOutlined,
    FastForwardOutlined,
    LoadingOutlined,
    ShopOutlined,
    UndoOutlined,
    ArrowsAltOutlined,
    ShrinkOutlined
} from "@ant-design/icons";
import {
    Avatar,
    Col,
    List,
    Popover,
    Row,
    Skeleton,
    Spin,
    Table,
    Collapse,
    Typography,
    Image,
    Tooltip,
    Button
} from "antd";
import GoogleMapReact from "google-map-react";
import { CountdownButton } from "./countdownButton/CountdownButton";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import apiKey from "../../../_core/apiKey";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import useIsFocused from "../../../hooks/useIsFocused";
import {
    AnnulmentButton,
    DescriptionContainer,
    DetailTag,
    FileImageFilledIcon,
    LeftButton,
    MapContainer,
    MapTools,
    Marker,
    MiddleButton,
    MinusCircleFilledIcon,
    OrderNumber,
    OrderTable,
    OrderTotalTime,
    MergedOrderStats,
    RightButton,
    SpinnerContainer,
    StepContainer,
    TableOverflow,
    TableTitle,
    TransferDescription,
    TransferInfoContainer,
    TransferLogList,
    PassButton
} from "./orderDetailStyles";
import {orderDetailPropType, updateOrderPropTypes} from "./orderDetailPropTypes";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {
    averageGeolocation,
    decodePolyline,
    formatPhoneNumber,
    calculateElapsedTime
} from "../../../utils/helpers";
import {
    courierStatusColors,
    orderTableColors,
    pickupColor,
    receiverColor,
    canceledColor,
    deliveredColor,
    defaultSlotCourierIds,
    mapDeliveryStatuses,
} from "../../../constants/constants";
import {TextBoxFormModal} from "../../_defaultComponents/TextBoxFormModal/TextBoxFormModal";
import config from "../../../_core/config";
import placeholder from "../../../public/images/person-placeholder.jpg";
import {ProofModal} from "./proof/ProofModal";
import {OrderNoteModal} from "./orderNote/OrderNoteModal";
import ProductTable from "../../_defaultComponents/ProductTable";
import OrderSteps from "./Steps/OrderSteps";
import SlotDetailCard from "./slot/SlotDetailCard";
import {PlatformDeliveryDetailTable} from "./platformDeliveryDetailTable/PlatformDeliveryDetailTable";
import {ReceiptPrinter} from "../../_defaultComponents/ReceiptPrinter/ReceiptPrinter";
import {NextStepModal} from "./nextStepModal/NextStepModal";

const {Column} = Table;
const {Text} = Typography;
const initialText = {
    label: "",
    title: ""
};

export const OrderDetail = ({deliveryId}: orderDetailPropType) => {
    const stores = useStore();
    const history = useHistory();
    const isFocused = useIsFocused();
    const {t, i18n} = useTranslation(["orderDetail", "deliveryStatuses", "paymentTypes"]);
    const [orderSteps, setOrderSteps] = useState([]);
    const [pickupSteps, setPickupSteps] = useState([]);
    const [returnSteps, setReturnSteps] = useState([]);
    const [allSteps, setAllSteps] = useState([]);
    const [canceled, setCanceled] = useState(false);
    const [returned, setReturned] = useState(false);
    const [delivered, setDelivered] = useState(false);
    const [inQueue, setInQueue] = useState(false);
    const [inPrep, setInPrep] = useState(false);
    const [assignedCourierId, setAssignedCourierId] = useState();
    const [countdown, setCountdown] = useState(0);
    const [recall, setRecall] = useState(true);
    const [transferLogRecall, setTransferLogRecall] = useState(true);
    const [loading, setLoading] = useState(false);
    const [colorIndexList, setColorIndexList] = useState({});
    const [hover, setHover] = useState("");
    const [modalType, setModalType] = useState("");
    const [text, setText] = useState(initialText);
    const [modalVisible, setModalVisible] = useState(false);
    const [submit, setSubmit] = useState();
    const [deliveryType, setDeliveryType] = useState();
    const [modalData, setModalData] = useState({deliveryId: "", orderId: ""});
    const [center, setCenter] = useState({
        lat: 41.11,
        lng: 29.11
    });
    const [map, setMap] = useState();
    const [maps, setMaps] = useState();
    const [mapMode, setMapMode] = useState("relative");
    const [drawnRoutes, setDrawnRoutes] = useState([]);
    const [totalTime, setTotalTime] = useState("");
    const [deliveredTime, setDeliveredTime] = useState("");
    const [latestLocationId, setLatestLocationId] = useState(null);
    const [exactPath, setExactPath] = useState([]);
    const [proofVisible, setProofVisible] = useState();
    const [proofData, setProofData] = useState({proof: {}, step: {}});
    const recallRef = useRef(recall);
    const transferLogRecallRef = useRef(transferLogRecall);
    const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
    const receiptRef = useRef();
    const [receiptModalVisible, setReceiptModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDate, setOrderDate] = useState(null);
    const [hideOrderError, setHideOrderError] = useState(false);
    const [orderNoteModalVisible, setOrderNoteModalVisible] = useState(false);
    const [operationNotes, setOperationNotes] = useState([]);
    recallRef.current = recall;
    transferLogRecallRef.current = transferLogRecall;
    const [isOpenNextStepModal, setIsOpenNextStepModal] = useState(false);
    const [nextStepData, setNextStepData] = useState(null);

    const [orderLoading, orderData, setOrderData, setOrderLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.orderStore.getOrder.bind(null, {
            deliveryId,
            hideError: hideOrderError
        }),
        dependencies: [recall]
    });

    const [transferLoading, transferData, setTransferData, setTransferLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.orderStore.getTransferLogs.bind(null, {
            deliveryId
        }),
        dependencies: [transferLogRecall]
    });

    const [
        deliveryLocationsDataLoading,
        deliveryLocationsData,
        setDeliveryLocationsData,
        setDeliveryLocationsDataLoading
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getDeliveryLocations.bind(null, {
            deliveryId,
            ...(latestLocationId !== null && {latestLocationId: latestLocationId})
        }),
        dependencies: [recall]
    });

    const orderNumber = orderData?.data?.delivery?.orders.map(x => x.orderNumber).join(", ");

    const estimation = orderData?.data?.estimation;

    useEffect(() => {
        if (orderData.status === 400) {
            history.push("/orders");
        } else if (isFocused) {
            let timeout = null;
            if (
                orderData?.data?.delivery?.deliveryState !== "delivered" &&
                orderData?.data?.delivery?.deliveryState !== "canceled" &&
                orderData?.data?.delivery?.deliveryState !== "rejected" &&
                orderData?.data?.delivery?.deliveryState !== "returned"
            ) {
                timeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 2000);
            }
            if (deliveryLocationsData.length > 0) {
                setLatestLocationId(deliveryLocationsData.slice(-1).pop().id);
            }

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    useEffect(() => {
        if (assignedCourierId) {
            setTransferLogRecall(!transferLogRecallRef.current);
        }
    }, [assignedCourierId]);

    useEffect(() => {
        setDeliveryType(orderData?.data?.delivery?.deliveryType);
        setTotalTime(orderData?.data?.delivery?.createdAt);
        if (orderData.status !== 200) {
            return;
        }

        if (
            orderData?.data?.delivery?.deliveryState === "canceled" ||
            orderData?.data?.delivery?.deliveryState === "rejected" ||
            orderData?.data?.delivery?.deliveryState === "returned"
        ) {
            const newSteps = orderData?.data?.steps.slice(
                0,
                orderData?.data?.delivery.currentStepIndex
            );
            setAllSteps(newSteps);
        } else {
            setAllSteps(orderData?.data?.steps);
        }

        let orderSteps = [];
        let pickupSteps = [];
        let returnSteps = [];
        let colorIndex = {};
        let index = 0;

        orderData.data.steps
            .sort((prev, curr) => prev.stepIndex - curr.stepIndex)
            .forEach(step => {
                if (step.deliveryState === "onDeliveryAddress") {
                    colorIndex[step.stepInfo._id] = orderTableColors[index];
                    orderSteps.push(step);
                }
                if (step.deliveryState === "onPickUpAddress") {
                    const products = [];
                    step?.stepInfo?.orders?.forEach(order => {
                        products.push(...order.products);
                    });
                    pickupSteps.push({...step, products});
                }
                if (step.deliveryState === "onReturnAddress") {
                    returnSteps.unshift(step);
                }
            });

        setDeliveredTime(orderData?.data?.steps[orderData?.data?.steps.length - 1]?.endDate);
        setColorIndexList(colorIndex);
        setOrderSteps(orderSteps);
        setPickupSteps(pickupSteps);
        setReturnSteps(returnSteps);

        const coordinates = orderSteps
            .map(step => ({
                latitude: step.stepInfo.receiverLocation.lat,
                longitude: step.stepInfo.receiverLocation.lon
            }))
            .concat(pickupSteps.map(step => step.stepInfo.location));

        const centerOfMap = averageGeolocation(coordinates);

        if (!isNaN(centerOfMap?.lat) && !isNaN(centerOfMap.lng)) setCenter(centerOfMap);
        setCanceled(orderData?.data?.delivery?.deliveryState === "canceled");
        setReturned(orderData?.data?.delivery?.deliveryState === "returned");
        setDelivered(orderData?.data?.delivery?.deliveryState === "delivered");
        setInQueue(orderData?.data?.delivery?.deliveryState === "inQueue");
        setInPrep(orderData?.data?.delivery?.deliveryState === "inPrep");
        setAssignedCourierId(orderData.data?.delivery?.assignedCourier?._id);
    }, [orderData]);

    useEffect(() => {
        if (modalType === "cancel") {
            setText({
                label: t("deliveryModal.cancelReasonLabel", {ns: "common"}),
                title: t("deliveryModal.cancelReasonTitle", {ns: "common"})
            });
            setSubmit(() => cancelDelivery);
        } else if (modalType === "splitOrder") {
            setText({
                label: t("modal.splitOrder.label", {ns: "orderDetail"}),
                title: t("modal.splitOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => splitOrder);
        } else if (modalType === "pass") {
            setText({
                label: t("modal.passOrder.label", {ns: "orderDetail"}),
                title: t("modal.passOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => passOrder);
        } else if (modalType === "cancelOrder") {
            setText({
                label: t("modal.cancelOrder.label", {ns: "orderDetail"}),
                title: t("modal.cancelOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => cancelOrder);
        } else if (modalType === "cancelOrderByNumber") {
            setText({
                label: t("modal.cancelOrder.label", {ns: "orderDetail"}),
                title: t("modal.cancelOrder.title", {ns: "orderDetail"})
            });
            setSubmit(() => cancelOrderByNumber);
        } else if (modalType !== "transfer") {
            setModalVisible(false);
        }
    }, [modalType]);

    useEffect(() => {
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
        };
    }, [mapMode]);

    const handleEsc = event => {
        if (event.keyCode === 27 && mapMode == "fixed") {
            setMapMode("relative");
        }
    };

    const getOperationNoteAndModal = async ({orderId, modalVisible}) => {
        const res = await stores.orderStore.getOperationNotes({
            orderId
        });
        setOperationNotes(res.data);
        setOrderNoteModalVisible(modalVisible);
    };

    const reOrder = ({deliveryId, orderId}: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.reOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.reOrder({
                    deliveryId,
                    orderId
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
            }
        });
    };

    const passOrder = ({deliveryId, orderId, reason}: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.passOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.passOrder({
                    deliveryId,
                    orderId,
                    reason
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const cancelOrder = ({deliveryId, orderId, reason}: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("deliveryModal.cancelConfirmContent", {ns: "common"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.cancelOrder({
                    deliveryId,
                    orderId,
                    reason
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };
    const cancelOrderByNumber = ({
        orderNumber,
        reason,
        resend = false,
        triggerInMinutes,
        forceCourierId
    }: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.cancelOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                const response = await stores.orderStore.cancelOrderByNumber({
                    vigoOrderNumber: modalData.vigoOrderNumber,
                    cancelText: reason,
                    resend,
                    triggerInMinutes,
                    forceCourierId
                });
                setRecall(!recallRef.current);
                await setOrderLoading(false);
                setModalVisible(false);
            }
        });
    };

    const splitOrder = async ({
        deliveryId,
        orderIds,
        allowMerge,
        forceCourierId,
        reason,
        triggerInMinutes
    }: updateOrderPropTypes) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.splitOrder.content", {ns: "orderDetail"}),
            onOk: async () => {
                await setOrderLoading(true);
                await stores.orderStore.splitOrder({
                    deliveryId,
                    orderIds,
                    allowMerge,
                    forceCourierId,
                    reason,
                    triggerInMinutes
                });
                const ordersCount = orderData?.data?.delivery?.orders?.length || 0;

                if (ordersCount == 0 || ordersCount == orderIds.length) {
                    history.push("/orders");
                } else {
                    setRecall(!recallRef.current);
                }
                await setOrderLoading(false);
                setModalVisible(false);
            },
            onCancel: () => {
                setHideOrderError(false);
            }
        });
    };

    const updateStep = async () => {
        const currentStep =
            allSteps[
                orderData.data?.steps?.slice(-1)[0]?.endDate && delivered
                    ? orderData.data.delivery.currentStepIndex
                    : orderData.data.delivery.currentStepIndex - 1
            ];

        if (
            currentStep.deliveryState === "onDeliveryAddress" &&
            Boolean(currentStep.startDate) &&
            Boolean(currentStep.trial)
        ) {
            if (currentStep.trial.startDate) {
                await stores.orderStore.trialEnd({courierId: assignedCourierId});
                await stores.orderStore.updateStep({
                    deliveryId,
                    courierId: orderData.data?.delivery?.assignedCourier?._id,
                    currentDeliveryState: orderData.data?.delivery?.deliveryState
                });
            } else {
                await stores.orderStore.trialStart({courierId: assignedCourierId});
            }
        } else {
            const updatedStepData = await stores.orderStore.updateStep({
                deliveryId,
                courierId: orderData.data?.delivery?.assignedCourier?._id,
                currentDeliveryState: orderData.data?.delivery?.deliveryState
            });
            if (updatedStepData?.data?.vigoPayment) {
                setNextStepData(updatedStepData);
                setIsOpenNextStepModal(true);
            }
        }

        setRecall(!recallRef.current);
        setLoading(false);
    };
    const triggerCallback = useCallback(async () => {
        await stores.orderStore.triggerCallback({
            deliveryId
        });
    }, [deliveryId, stores.orderStore]);

    const cancelDelivery = ({reason}: deliveryUpdateWithTextPropType) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("deliveryModal.cancelConfirmContent", {ns: "common"}),
            onOk: async () => {
                await setOrderLoading(true);
                stores.orderStore.cancelDelivery({
                    cancelText: reason,
                    deliveryId
                });
                setRecall(!recallRef.current);
                setModalVisible(false);
                await setOrderLoading(false);
            }
        });
    };

    /* const repeatDelivery = async () => {
        const company = await stores.companyStore.getCompany({
            companyId: stores.meStore.company._id,
            withSubCompanies: false
        });
        const auth = await stores.apiRequestStore.getAuthToken({
            data: {apiSecret: company.data.apiSecret},
            apiKey: company.data.apiKey
        });
        const body = {
            repeatDelivery: true,
            pickupInfos: orderData.data.delivery.pickupInfos.map((info) => {
                const {index, name, phone, address, location} = info;

                return {index, name, phone, address, location};
            }),
            orders: orderData.data.delivery.orders.map((order, i) => {
                if (!order.orderNumber) {
                    order.orderNumber = moment().format("mmssSSS");
                }

                let {
                    index,
                    products,
                    receiverName,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    orderNumber
                } = order;
                return {
                    index,
                    products,
                    receiverName,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    orderNumber
                };
            }),
            courierType: orderData.data.delivery.courierType
        };
        await stores.apiRequestStore.createDelivery({
            data: body,
            apiKey: company.data.apiKey,
            token: auth.token
        });
    }; */

    const fitBounds = ({map, pickupSteps, orderSteps, courierLocation, estimationRoutes}) => {
        if (!map) return;

        const bounds = new window.google.maps.LatLngBounds();
        if (pickupSteps) {
            pickupSteps.map(pickup => {
                boundsExtender({
                    bounds,
                    lat: pickup?.stepInfo?.location?.latitude,
                    lng: pickup?.stepInfo?.location?.longitude
                });
            });
        }

        if (orderSteps) {
            orderSteps.map(order => {
                boundsExtender({
                    bounds,
                    lat: order?.stepInfo?.receiverLocation?.lat,
                    lng: order?.stepInfo?.receiverLocation?.lng
                });
            });
        }

        if (
            courierLocation &&
            !["delivered", "canceled", "returned"].includes(
                orderData?.data?.delivery?.deliveryState
            )
        ) {
            boundsExtender({
                bounds,
                lat: courierLocation.coordinates[1],
                lng: courierLocation.coordinates[0]
            });
        }

        if (estimationRoutes) {
            estimationRoutes
                .filter(r => !!r.encodedPolylines)
                .map(route => {
                    const path = decodePolyline(route.encodedPolylines);
                    path.map(coordinate => {
                        boundsExtender({bounds, lat: coordinate.lat, lng: coordinate.lng});
                    });
                });
        }

        if (bounds.isEmpty()) return;

        map.fitBounds(bounds);
    };

    const boundsExtender = ({bounds, lat, lng}) => {
        if (lat === undefined || lng === undefined) return;
        bounds.extend({lat, lng});
    };

    useEffect(() => {
        renderPolylines(map, maps);
    }, [estimation?.routes.length]);

    useEffect(() => {
        renderPolylinesExact(map, maps);
    }, [deliveryLocationsData]);

    const renderPolylines = (map, maps) => {
        let tempDrawnRoutes = [];
        if (map && maps) {
            if (!estimation?.routes) return;
            drawnRoutes.forEach(poly => {
                poly.setMap(null);
            });

            for (let i = 0; i < estimation.routes?.length; i++) {
                if (!estimation.routes[i].encodedPolylines) {
                    continue;
                }
                const routeColor = "#000000";
                let routeTopColor;
                if (i < estimation.routes.length - 1) {
                    if (estimation.routes[i + 1].routeType === "fromDelivery")
                        routeTopColor = "#ff9a66";
                    else if (estimation.routes[i + 1].routeType === "fromPickup")
                        routeTopColor = "#5abeea";
                } else {
                    routeTopColor = "#ff9a66";
                }
                const path = decodePolyline(estimation.routes[i].encodedPolylines);

                let polylineBorder = new maps.Polyline({
                    path: path,
                    strokeColor: routeColor,
                    strokeWeight: 5
                });
                let polyline = new maps.Polyline({
                    path: path,
                    strokeColor: routeTopColor,
                    strokeWeight: 3
                });

                polylineBorder.setMap(map);
                polyline.setMap(map);
                tempDrawnRoutes = drawnRoutes;
                tempDrawnRoutes.push(polylineBorder);
                tempDrawnRoutes.push(polyline);
                setDrawnRoutes(tempDrawnRoutes);
            }
        }
    };
    const renderPolylinesExact = (map, maps) => {
        let tempDrawnRoutes = [];
        if (map && maps) {
            if (deliveryLocationsData.length > 0) {
                const newLocations = [];

                deliveryLocationsData.forEach(item =>
                    newLocations.push({lat: item.coordinates[1], lng: item.coordinates[0]})
                );
                if (
                    newLocations.length === 1 &&
                    isEqual(exactPath[exactPath.length - 1], newLocations[0])
                ) {
                    return;
                }
                setExactPath(exactPath => exactPath.concat(newLocations));

                let polylineBorderExactPath = new maps.Polyline({
                    path: exactPath,
                    strokeColor: "#000000",
                    strokeWeight: 5
                });

                let polylineExactPath = new maps.Polyline({
                    path: exactPath,
                    strokeColor: "#54E626",
                    strokeWeight: 3
                });

                polylineBorderExactPath.setMap(map);
                polylineExactPath.setMap(map);
                tempDrawnRoutes = drawnRoutes;
                tempDrawnRoutes.push(polylineBorderExactPath);
                tempDrawnRoutes.push(polylineExactPath);
                setDrawnRoutes(tempDrawnRoutes);
            }
        }
    };

    const renderProofIcon = step => {
        let {proofs} = step;
        proofs = proofs.filter(p => p.label == proofs[0].label);
        let proofIcons = null;
        const emptyProof = () => <MinusCircleFilledIcon color={courierStatusColors.unavailable} />;
        const filledProof = () => {
            return (
                <>
                    {proofs.map(proof => {
                        if (
                            (step.deliveryState === "onDeliveryAddress" &&
                                step.stepInfo.orderNumber === proof.orderNumber) ||
                            (step.deliveryState === "onPickUpAddress" &&
                                step.stepInfo.orders.filter(
                                    order => order.orderNumber === proof.orderNumber
                                ).length > 0)
                        ) {
                            return (
                                <FileImageFilledIcon
                                    key={proof?._id}
                                    onClick={() => {
                                        const step =
                                            allSteps.filter(
                                                step => step?.stepIndex === proof?.stepIndex
                                            )[0] || {};
                                        const receiverName = getReceiverName(proof.orderNumber);
                                        setProofData({
                                            proof,
                                            step: {...step, receiverName}
                                        });
                                        setProofVisible(step.deliveryState);
                                    }}
                                    color={courierStatusColors.available}
                                />
                            );
                        }
                    })}
                </>
            );
        };
        const getPickUpAddress = state => allSteps.filter(step => step?.deliveryState === state);
        const getDeliveryAddress = (state, orderNumber) =>
            allSteps.filter(
                step => step?.stepInfo?.orderNumber === orderNumber && step?.deliveryState === state
            );
        const getReceiverName = orderNumber =>
            getDeliveryAddress("onDeliveryAddress", orderNumber)[0]?.stepInfo?.receiverName;
        const getProof = (step, orderNumber) =>
            step?.proofs.filter(proof => proof.orderNumber === orderNumber)[0];

        if (proofs) {
            proofIcons = filledProof();
        } else {
            proofIcons = emptyProof();
        }

        return (
            <>
                {proofIcons}
                {proofVisible == step.deliveryState ? (
                    <ProofModal
                        visible={proofVisible == step.deliveryState}
                        data={proofData}
                        handleClose={() => setProofVisible(null)}
                        changeDeliveryState={(orderNumber, deliveryState) => {
                            const selectedStep =
                                deliveryState === "onPickUpAddress"
                                    ? getPickUpAddress(deliveryState)
                                    : getDeliveryAddress(deliveryState, orderNumber);
                            const receiverName = getReceiverName(orderNumber);
                            setProofData({
                                proof: getProof(selectedStep[0], orderNumber),
                                step: {...selectedStep[0], receiverName},
                                orderNumber
                            });
                        }}
                    />
                ) : null}
            </>
        );
    };
    const renderMergedOrderStats = () => {
        if (
            delivered ||
            canceled ||
            returned ||
            inPrep ||
            inQueue ||
            orderData?.data?.delivery?.orders?.length < 2
        ) {
            return null;
        }
        let content = "";
        //active orders and merged orders
        let totalOrdersCount = orderData?.data?.delivery?.orders?.length || 0;
        let deliveredOrdersCount = 0;
        let pickedUpOrdersCount = 0;

        orderData.data?.delivery?.orders?.forEach(order => {
            const hasPickedUp = orderData?.data?.delivery?.pickupInfos?.some(
                pI => pI._id.toString() === order.pickupInfoId.toString() && pI.state === "picked"
            );
            if (hasPickedUp) {
                pickedUpOrdersCount++;
            }

            if (order.state === "delivered") {
                deliveredOrdersCount++;
            }
        });

        if (pickedUpOrdersCount > 0 || deliveredOrdersCount > 0) {
            content = (() => (
                <>
                    {t("mergedOrdersStatus.picked", {
                        ns: "orderDetail",
                        pickedUpCount: pickedUpOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                    ,
                    <br />
                    {t("mergedOrdersStatus.delivered", {
                        ns: "orderDetail",
                        deliveredCount: deliveredOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                </>
            ))();
        } else {
            return null;
        }

        return (
            <>
                <MergedOrderStats>{content}</MergedOrderStats>
                <br />
            </>
        );
    };

    const renderReceiverMarkers = orderSteps => {
        return orderSteps.map((order, index) => {
            const orders = orderData?.data?.delivery?.orders;
            const orderItem =
                orders.length > 0 && orders.find(o => o.orderNumber == order.stepInfo?.orderNumber);
            let color = receiverColor;
            switch (orderItem?.state) {
                case "canceled":
                case "returned":
                    color = canceledColor;
                    break;
                case "delivered":
                    color = deliveredColor;
                    break;
                default:
                    color = receiverColor;
            }
            return (
                <Marker
                    key={"order" + index}
                    size={20}
                    icon={<EnvironmentOutlined />}
                    lat={order?.stepInfo?.receiverLocation?.lat}
                    lng={order?.stepInfo?.receiverLocation?.lon}
                    text={`${t("fields.customer", {ns: "common"})}: ${
                        order?.stepInfo?.receiverName
                    }`}
                    color={color}
                    data-testid="receiverMarker"
                />
            );
        });
    };

    const toggleMapMode = () => {
        if (mapMode == "fixed") {
            setMapMode("relative");
        } else {
            setMapMode("fixed");
        }
    };

    const courierIconBackgroundColor = useMemo(() => {
        let colorStatueName = orderData.data?.courierLocation?.status;
        if (colorStatueName === "busy") {
            colorStatueName = orderData.data.delivery.deliveryState;
        }
        return courierStatusColors[colorStatueName];
    }, [orderData?.data?.courierLocation?.status, orderData?.data?.delivery?.deliveryState]);

    const sortedSteps = useMemo(
        () =>
            orderData?.data?.steps?.sort((a, b) =>
                new Date(b.stepIndex) > new Date(a.stepIndex) ? -1 : 1
            ),
        [orderData?.data?.steps]
    );

    const handleOrderNoteSave = async note => {
        await stores.orderStore.updateOrderOperationNote({
            deliveryId,
            orderId: selectedOrder._id,
            note
        });
        setRecall(!recallRef.current);
        setOrderNoteModalVisible(false);
    };

    return (
        <React.Fragment>
            {!orderData.data || orderData.status !== 200 ? (
                <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
            ) : (
                <React.Fragment>
                    <TextBoxFormModal
                        isVisible={modalVisible}
                        setVisible={() => setModalVisible(!modalVisible)}
                        okFunction={submit}
                        label={text.label}
                        title={text.title}
                        modalType={modalType}
                        modalData={modalData}
                        deliveryId={deliveryId}
                        deliveryData={orderData.data}
                        cancelFn={() => {
                            setHideOrderError(false);
                            setModalType("");
                        }}
                        deliveryType={deliveryType}
                    />

                    <Row gutter={[30, 30]} data-testid="orderDetails">
                        <Col span={14}>
                            <OrderNumber data-testid="orderNumber">
                                {t("fields.orderNumber", {ns: "common"})}:{" "}
                                {orderNumber ? orderNumber : "-"}
                            </OrderNumber>
                            <Row gutter={[0, 30]}>
                                <MapContainer mode={mapMode} data-testid="map">
                                    <MapTools
                                        onClick={e => {
                                            toggleMapMode();
                                            e.currentTarget.blur();
                                        }}
                                    >
                                        {mapMode == "relative" ? (
                                            <ArrowsAltOutlined />
                                        ) : (
                                            <ShrinkOutlined />
                                        )}
                                    </MapTools>
                                    <GoogleMapReact
                                        onGoogleApiLoaded={({map, maps}) => {
                                            setMap(map);
                                            setMaps(maps);
                                            renderPolylines(map, maps);
                                            if (
                                                [
                                                    "delivered",
                                                    "onPickUpAddress",
                                                    "onDeliveryAddress"
                                                ].includes(orderData.data.delivery.deliveryState)
                                            ) {
                                                renderPolylinesExact(map, maps);
                                            }
                                            fitBounds({
                                                map,
                                                pickupSteps,
                                                orderSteps,
                                                courierLocation: !defaultSlotCourierIds.includes(
                                                    orderData.data?.delivery?.assignedCourier?._id.toString()
                                                )
                                                    ? orderData.data?.courierLocation?.location
                                                    : null,
                                                estimationRoutes: estimation?.routes
                                            });
                                        }}
                                        heatmapLibrary={true}
                                        center={center}
                                        bootstrapURLKeys={{
                                            key: apiKey,
                                            language: i18n.resolvedLanguage || "tr"
                                        }}
                                        yesIWantToUseGoogleMapApiInternals
                                        defaultZoom={13}
                                        options={{
                                            fullscreenControl: false,
                                            gestureHandling: "greedy"
                                        }}
                                    >
                                        {!defaultSlotCourierIds.includes(
                                            orderData.data?.delivery?.assignedCourier?._id.toString()
                                        ) &&
                                            !["delivered", "canceled", "returned"].includes(
                                                orderData?.data?.delivery?.deliveryState
                                            ) &&
                                            orderData.data.courierLocation && (
                                                <Marker
                                                    size={20}
                                                    type="courier"
                                                    data-testid="courierMarker"
                                                    lat={
                                                        orderData.data.courierLocation.location
                                                            .coordinates[1]
                                                    }
                                                    lng={
                                                        orderData.data.courierLocation.location
                                                            .coordinates[0]
                                                    }
                                                    icon={
                                                        <CourierIcon
                                                            courierType={
                                                                orderData.data.courierLocation.type
                                                            }
                                                        />
                                                    }
                                                    text={`${t("fields.courier", {
                                                        ns: "common"
                                                    })}: ${
                                                        orderData.data?.delivery?.assignedCourier
                                                            ?.fullname
                                                    }`}
                                                    color={courierIconBackgroundColor}
                                                />
                                            )}
                                        {pickupSteps.map((pickup, index) => (
                                            <Marker
                                                key={"pickup" + index}
                                                size={20}
                                                lat={pickup?.stepInfo?.location?.latitude}
                                                lng={pickup?.stepInfo?.location?.longitude}
                                                icon={<ShopOutlined />}
                                                text={`${t("fields.warehouse", {ns: "common"})}: ${
                                                    pickup?.stepInfo?.name
                                                }`}
                                                color={pickupColor}
                                                data-testid="pickupMarker"
                                            />
                                        ))}
                                        {renderReceiverMarkers(orderSteps)}
                                    </GoogleMapReact>
                                </MapContainer>
                            </Row>
                            <TableTitle>
                                {deliveryType === "productReturn"
                                    ? t("fields.returnPoints", {ns: "common"})
                                    : t("fields.deliveryPoints", {ns: "common"})}
                            </TableTitle>
                            <TableOverflow>
                                <OrderTable
                                    data-testid="orderTable"
                                    onRow={(record, index) => {
                                        return {
                                            onMouseEnter: event => {
                                                setHover(record.stepInfo._id);
                                            },
                                            style: {
                                                backgroundColor:
                                                    (record.stepInfo.passed &&
                                                        (hover === record.stepInfo._id
                                                            ? colorIndexList[record.stepInfo._id]
                                                            : "white")) ||
                                                    (record.reorderOf &&
                                                        (hover === record.stepInfo._id
                                                            ? colorIndexList[record.stepInfo._id]
                                                            : "white"))
                                            },
                                            onMouseLeave: event => {
                                                setHover("");
                                            }
                                        };
                                    }}
                                    rowKey="_id"
                                    dataSource={orderSteps}
                                    pagination={false}
                                    expandable={{
                                        expandedRowRender: record => {
                                            const order = orderData?.data?.delivery?.orders.filter(
                                                i =>
                                                    i?.orderNumber === record?.stepInfo?.orderNumber
                                            );

                                            return (
                                                <ProductTable
                                                    showNote
                                                    dataSource={order ? order[0].products : []}
                                                    pagination={false}
                                                    note={record.stepInfo.note}
                                                    price={record.stepInfo.price}
                                                    paymentType={record.stepInfo.paymentType}
                                                    showPaymentInfo={true}
                                                    barcode={record.stepInfo.barcode}
                                                    showBarcode={true}
                                                />
                                            );
                                        }
                                    }}
                                >
                                    <Column
                                        title={t("fields.hour", {ns: "common"})}
                                        dataIndex="stepInfo"
                                        render={text => {
                                            const orders = orderData?.data?.delivery?.orders || [];
                                            const order = orders?.find(
                                                order => order?._id === text?._id
                                            );
                                            return moment(order?.createdAt).format("HH:mm:ss");
                                        }}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.companyName", {ns: "common"})}
                                        render={value => {
                                            const orders = orderData?.data?.delivery?.orders || [];
                                            const order = orders?.find(
                                                order => order?._id === value?._id
                                            );
                                            const tooltipTitle =
                                                "Firma: " +
                                                order?.pickupInfoCompanyName +
                                                " - " +
                                                "Şube: " +
                                                order?.pickupInfoName;

                                            const companyName = order?.pickupInfoCompanyName;
                                            return (
                                                <Tooltip title={tooltipTitle}>
                                                    <span data-testid="receiverCompany">
                                                        {companyName ? companyName + "..." : ""}
                                                    </span>
                                                </Tooltip>
                                            );
                                        }}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={
                                            deliveryType === "productReturn"
                                                ? t("fields.returnPoint", {ns: "common"})
                                                : t("fields.customerName", {ns: "common"})
                                        }
                                        render={text => (
                                            <span data-testid="receiverName">
                                                {text.receiverName}
                                            </span>
                                        )}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.phone", {ns: "common"})}
                                        render={text => (
                                            <span data-testid="receiverPhone">
                                                {formatPhoneNumber(
                                                    text.receiverPhone,
                                                    text.receiverPhone
                                                )}
                                            </span>
                                        )}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.address", {ns: "common"})}
                                        render={stepInfo => {
                                            const regionGroupName = stepInfo.regionGroupName;
                                            const regionGroupNameVisibility =
                                                stores.meStore.checkRole(
                                                    "notVisibleForCompanyUser"
                                                ) && regionGroupName?.length > 0;
                                            return (
                                                <span data-testid="receiverAddress">
                                                    {stepInfo.receiverAddress}
                                                    {regionGroupNameVisibility && (
                                                        <b> [{stepInfo.regionGroupName}]</b>
                                                    )}
                                                </span>
                                            );
                                        }}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={t("fields.addressDetail", {ns: "common"})}
                                        render={stepInfo => (
                                            <span data-testid="receiverAddressDetail">
                                                {stepInfo.receiverAddressDetail}
                                            </span>
                                        )}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={"Mesafe Bilgisi"}
                                        render={(stepInfo, record) => (
                                            <Tooltip title={record?.stepInfo?.distance + " metre"}>
                                                <span
                                                    data-testid="distance"
                                                    style={{
                                                        borderBottom: "1px dotted #999"
                                                    }}
                                                >
                                                    {record?.stepInfo?.distance
                                                        ? (record.stepInfo.distance / 1000).toFixed(
                                                              2
                                                          ) + " km"
                                                        : "-"}
                                                </span>
                                            </Tooltip>
                                        )}
                                    />
                                    <Column
                                        dataIndex="paymentType"
                                        title={"Ödeme Yöntemi"}
                                        render={(text, record) => {
                                            const paymentType = record?.stepInfo?.paymentType;
                                            const getPaymentText = type => {
                                                switch (type) {
                                                    case "paid":
                                                        return "Ödendi";
                                                    case "creditCard":
                                                        return "Kredi Kartı";
                                                    case "cash":
                                                        return "Nakit";
                                                    case "ticketRestaurantCard":
                                                        return "Ticket Restaurant Kart";
                                                    case "ticketRestaurantVoucher":
                                                        return "Ticket Restaurant Fiş";
                                                    case "sodexoRestaurantPass":
                                                        return "Sodexo Restaurant Pass";
                                                    case "sodexoRestaurantPassMobilePayment":
                                                        return "Sodexo Restaurant Pass Mobil";
                                                    case "sodexoOnline":
                                                        return "Sodexo Online";
                                                    case "sodexoCard":
                                                        return "Sodexo Kart";
                                                    case "multinet":
                                                        return "Multinet";
                                                    case "setCard":
                                                        return "SetCard";
                                                    case "yemekmatikYemekKart":
                                                        return "Yemekmatik Yemek Kart";
                                                    case "metropolCard":
                                                        return "Metropol Kart";
                                                    case "cioCard":
                                                        return "CIO Kart";
                                                    case "payeCard":
                                                        return "Paye Kart";
                                                    case "winWin":
                                                        return "WinWin";
                                                    case "tokenFlex":
                                                        return "Token Flex";
                                                    case "unspecified":
                                                        return "Belirtilmemiş";
                                                    default:
                                                        return paymentType || "-";
                                                }
                                            };

                                            return (
                                                <span data-testid="paymentType">
                                                    {getPaymentText(paymentType)}
                                                </span>
                                            );
                                        }}
                                    />
                                    <Column
                                        dataIndex="stepInfo"
                                        title={"Not"}
                                        render={(stepInfo, record) => {
                                            const orders = orderData?.data?.delivery?.orders || [];
                                            const order = orders?.find(
                                                order => order?._id === stepInfo?._id
                                            );
                                            return (
                                                <span data-testid="operationNote">
                                                    {order?.operationNote || ""}
                                                </span>
                                            );
                                        }}
                                    />
                                    {orderData?.data?.delivery?.orders.some(
                                        order => order?.receipt !== null
                                    ) ? (
                                        <Column
                                            dataIndex="stepInfo"
                                            title={t("fields.orderReceipt", {ns: "common"})}
                                            render={stepInfo => {
                                                const receipt = orderData?.data?.delivery?.orders.find(
                                                    o => o._id.toString() === stepInfo._id
                                                )?.receipt;

                                                return (
                                                    <Image
                                                        src={receipt?.publicUrl}
                                                        width={200}
                                                        height={200}
                                                        style={{
                                                            objectFit: "contain"
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    ) : null}
                                    <Column
                                        title="Ödeme Tipi"
                                        key="payment"
                                        render={orders => {
                                            return (
                                                <span>
                                                    {orders?.stepInfo?.vigoPayment
                                                        ? "VigoPos"
                                                        : "-"}
                                                </span>
                                            );
                                        }}
                                    />
                                    {stores.meStore.checkRole("updateOrder") &&
                                        orderData.data.delivery.orders.length > 0 && (
                                            <Column
                                                align="center"
                                                dataIndex="stepInfo"
                                                title={t("actions.action", {ns: "common"})}
                                                render={(text, row) => {
                                                    const currentStepIndex =
                                                        orderData.data.delivery.currentStepIndex;
                                                    const lastOrderStepIndex =
                                                        orderSteps[orderSteps.length - 1].stepIndex;

                                                    const passButton =
                                                        row.stepIndex === currentStepIndex &&
                                                        currentStepIndex !== lastOrderStepIndex;
                                                    const reOrderButton =
                                                        row.stepInfo.passed &&
                                                        !row.stepInfo.reOrdered;
                                                    const isReordered =
                                                        orderData.data?.delivery?.orders.find(
                                                            o => o._id.toString() === text._id
                                                        )?.state === "reOrdered";

                                                    const disabled =
                                                        delivered ||
                                                        canceled ||
                                                        orderData.data.delivery.deliveryState ===
                                                            "onTheWayForReturn" ||
                                                        orderData.data.delivery.deliveryState ===
                                                            "onReturnAddress";

                                                    const orders =
                                                        orderData?.data?.delivery?.orders || [];
                                                    const order = orders?.find(
                                                        order => order?._id === row?.stepInfo?._id
                                                    );
                                                    const companyName =
                                                        order?.pickupInfoCompanyName;

                                                    return (
                                                        <Row>
                                                            {row.stepInfo.passed && (
                                                                <Col span={24}>
                                                                    <Popover
                                                                        content={
                                                                            <span>
                                                                                {row.stepInfo
                                                                                    .passReason
                                                                                    ? row.stepInfo
                                                                                          .passReason
                                                                                    : t(
                                                                                          "passReasonProblem",
                                                                                          {
                                                                                              ns:
                                                                                                  "orderDetail"
                                                                                          }
                                                                                      )}
                                                                            </span>
                                                                        }
                                                                        title={t("reason", {
                                                                            ns: "orderDetail"
                                                                        })}
                                                                        placement="rightTop"
                                                                        trigger="hover"
                                                                    >
                                                                        <DetailTag
                                                                            icon={
                                                                                <CheckCircleOutlined />
                                                                            }
                                                                            color="success"
                                                                        >
                                                                            {t("passed", {
                                                                                ns: "orderDetail"
                                                                            })}
                                                                        </DetailTag>
                                                                    </Popover>
                                                                </Col>
                                                            )}
                                                            <Col span={24}>
                                                                {stores.meStore.checkRole(
                                                                    "passOrder"
                                                                ) ? (
                                                                    <PassButton
                                                                        data-testid={`${
                                                                            reOrderButton
                                                                                ? "sendAgain"
                                                                                : "pass"
                                                                        }Button`}
                                                                        disabled={
                                                                            (!reOrderButton &&
                                                                                !passButton) ||
                                                                            isReordered ||
                                                                            disabled
                                                                        }
                                                                        onClick={() => {
                                                                            if (reOrderButton) {
                                                                                reOrder({
                                                                                    deliveryId,
                                                                                    orderId:
                                                                                        text._id
                                                                                });
                                                                            }
                                                                            if (passButton) {
                                                                                setModalVisible(
                                                                                    !modalVisible
                                                                                );
                                                                                setModalType(
                                                                                    "pass"
                                                                                );
                                                                                setModalData({
                                                                                    deliveryId,
                                                                                    orderId:
                                                                                        text._id
                                                                                });
                                                                            }
                                                                        }}
                                                                    >
                                                                        {reOrderButton
                                                                            ? t(
                                                                                  "actions.sendAgain",
                                                                                  {
                                                                                      ns:
                                                                                          "orderDetail"
                                                                                  }
                                                                              )
                                                                            : t("actions.pass", {
                                                                                  ns: "orderDetail"
                                                                              })}
                                                                    </PassButton>
                                                                ) : (
                                                                    <></>
                                                                )}

                                                                <Row
                                                                    gutter={[10, 10]}
                                                                    justify="center"
                                                                    align="middle"
                                                                >
                                                                    <Col>
                                                                        <Button
                                                                            type="primary"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    "#FFD700",
                                                                                color: "black",
                                                                                borderColor:
                                                                                    "#FFD700"
                                                                            }}
                                                                            onClick={() => {
                                                                                setSelectedOrder(
                                                                                    order
                                                                                );
                                                                                getOperationNoteAndModal(
                                                                                    {
                                                                                        orderId:
                                                                                            text._id,
                                                                                        modalVisible: true
                                                                                    }
                                                                                );
                                                                            }}
                                                                        >
                                                                            {orderData?.data?.delivery?.orders.find(
                                                                                order =>
                                                                                    order?._id ===
                                                                                    text._id
                                                                            )?.operationNote
                                                                                ? "Not Düzenle"
                                                                                : "Not Ekle"}
                                                                        </Button>
                                                                    </Col>
                                                                    <Col>
                                                                        <Button
                                                                            type="primary"
                                                                            onClick={() => {
                                                                                setSelectedOrder(
                                                                                    order
                                                                                );
                                                                                setOrderDate(
                                                                                    row?.createdAt
                                                                                );
                                                                                setReceiptModalVisible(
                                                                                    true
                                                                                );
                                                                            }}
                                                                        >
                                                                            Fişi görüntüle
                                                                        </Button>
                                                                    </Col>
                                                                    <Col>
                                                                        <AnnulmentButton
                                                                            type="danger"
                                                                            data-testid="cancelButton"
                                                                            disabled={
                                                                                row.stepInfo
                                                                                    .canceled ||
                                                                                orderData.data
                                                                                    .delivery
                                                                                    .currentStepIndex >
                                                                                    row.stepIndex ||
                                                                                [
                                                                                    "delivered",
                                                                                    "canceled"
                                                                                ].includes(
                                                                                    orderData.data
                                                                                        .delivery
                                                                                        .deliveryState
                                                                                )
                                                                            }
                                                                            onClick={() => {
                                                                                setModalVisible(
                                                                                    !modalVisible
                                                                                );
                                                                                setModalType(
                                                                                    "cancelOrderByNumber"
                                                                                );
                                                                                setModalData({
                                                                                    vigoOrderNumber:
                                                                                        text.vigoOrderNumber,
                                                                                    orderNumber:
                                                                                        text.orderNumber
                                                                                });
                                                                            }}
                                                                        >
                                                                            {t("actions.cancel", {
                                                                                ns: "orderDetail"
                                                                            })}
                                                                        </AnnulmentButton>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                    );
                                                }}
                                            />
                                        )}
                                </OrderTable>
                                <ReceiptPrinter
                                    key={selectedOrder?._id}
                                    receipt={selectedOrder?.receipt}
                                    receiptModalVisible={receiptModalVisible}
                                    setReceiptModalVisible={setReceiptModalVisible}
                                    order={selectedOrder}
                                    orderData={orderData}
                                    receiptRef={receiptRef}
                                    setIsReceiptPrinting={setIsReceiptPrinting}
                                    isReceiptPrinting={isReceiptPrinting}
                                />
                            </TableOverflow>
                            <TableTitle>
                                {deliveryType === "productReturn"
                                    ? t("fields.returnPickupPoints", {ns: "common"})
                                    : t("fields.pickupPoints", {ns: "common"})}
                            </TableTitle>
                            <Table
                                rowKey="_id"
                                dataSource={pickupSteps}
                                pagination={false}
                                data-testid="collectingPointTable"
                                expandable={{
                                    expandedRowRender: record => {
                                        return (
                                            <ProductTable
                                                dataSource={record.products}
                                                pagination={false}
                                            />
                                        );
                                    }
                                }}
                            >
                                <Column
                                    dataIndex="stepInfo"
                                    title={
                                        deliveryType === "productReturn"
                                            ? t("fields.customerName", {ns: "common"})
                                            : t("fields.warehouseName", {ns: "common"})
                                    }
                                    render={text => <span data-testid="name">{text.name}</span>}
                                />
                                <Column
                                    dataIndex="stepInfo"
                                    title={t("fields.phone", {ns: "common"})}
                                    render={text => (
                                        <span data-testid="phone">
                                            {formatPhoneNumber(text.phone)}
                                        </span>
                                    )}
                                />
                                <Column
                                    dataIndex="stepInfo"
                                    title={t("fields.address", {ns: "common"})}
                                    render={text => (
                                        <span data-testid="address">{text.address}</span>
                                    )}
                                />
                            </Table>
                            {orderData.data.delivery.orders &&
                            orderData.data.delivery.orders[0] &&
                            orderData.data.delivery.orders[0].platformPickingTime ? (
                                <PlatformDeliveryDetailTable
                                    orders={orderData.data.delivery.orders}
                                />
                            ) : (
                                ""
                            )}
                            {returnSteps.length >= 1 && (
                                <>
                                    <TableTitle>
                                        {t("returnPoints", {ns: "orderDetail"})}
                                    </TableTitle>
                                    <Table
                                        rowKey="_id"
                                        dataSource={returnSteps}
                                        pagination={false}
                                        data-testid="returnSteps"
                                        expandable={{
                                            expandedRowRender: record => {
                                                return (
                                                    <ProductTable
                                                        dataSource={record.stepInfo.products}
                                                        pagination={false}
                                                    />
                                                );
                                            }
                                        }}
                                    >
                                        <Column
                                            dataIndex="stepInfo"
                                            title={
                                                deliveryType === "productReturn"
                                                    ? t("fields.customerName", {ns: "common"})
                                                    : t("fields.warehouseName", {ns: "common"})
                                            }
                                            data-testid="name"
                                            render={text => text.name}
                                        />
                                        <Column
                                            dataIndex="stepInfo"
                                            title={t("fields.phone", {ns: "common"})}
                                            data-testid="phone"
                                            render={text => formatPhoneNumber(text.phone)}
                                        />
                                        <Column
                                            dataIndex="stepInfo"
                                            title={t("fields.address", {ns: "common"})}
                                            data-testid="address"
                                            render={text => text.address}
                                        />
                                    </Table>
                                </>
                            )}
                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                orderData.data.courierLocation && (
                                    <>
                                        <TableTitle>
                                            {t("fields.courier", {ns: "common"})}
                                        </TableTitle>
                                        <DescriptionContainer column={2} bordered>
                                            <DescriptionContainer.Item
                                                label={t("fields.courierName", {ns: "common"})}
                                            >
                                                <span data-testid="courierName">
                                                    {
                                                        orderData.data?.delivery?.assignedCourier
                                                            ?.fullname
                                                    }
                                                </span>
                                            </DescriptionContainer.Item>

                                            <DescriptionContainer.Item
                                                label={t("fields.drivingTraining", {ns: "common"})}
                                                data-testvalue={
                                                    orderData.data?.delivery?.assignedCourier
                                                        ?.trained
                                                }
                                            >
                                                <span data-testid="courierTrained">
                                                    {orderData.data?.delivery?.assignedCourier
                                                        ?.trained ? (
                                                        <CheckCircleOutlined />
                                                    ) : (
                                                        <CloseCircleOutlined />
                                                    )}
                                                </span>
                                            </DescriptionContainer.Item>

                                            <DescriptionContainer.Item
                                                label={t("fields.phone", {ns: "common"})}
                                            >
                                                <span data-testid="courierPhone">
                                                    {formatPhoneNumber(
                                                        orderData.data?.delivery?.assignedCourier
                                                            ?.cellPhone
                                                    )}
                                                </span>
                                            </DescriptionContainer.Item>
                                            <DescriptionContainer.Item
                                                label={t("fields.email", {ns: "common"})}
                                                data-testid="courierEmail"
                                            >
                                                {orderData.data?.delivery?.assignedCourier?.email}
                                            </DescriptionContainer.Item>
                                        </DescriptionContainer>
                                    </>
                                )}
                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                orderData.data.courierLocation && (
                                    <>
                                        <TableTitle>
                                            {t("fields.company", {ns: "common"})}
                                        </TableTitle>
                                        <DescriptionContainer column={2} bordered>
                                            <DescriptionContainer.Item
                                                span={2}
                                                label={t("fields.companyName", {ns: "common"})}
                                                data-testid="companyName"
                                            >
                                                {orderData.data.delivery.company.companyName}
                                            </DescriptionContainer.Item>
                                            <DescriptionContainer.Item
                                                label={t("fields.createdDate", {ns: "common"})}
                                                data-testid="companyCreatedAt"
                                            >
                                                {moment(orderData.data.delivery.company.createdAt)
                                                    .locale(i18n.resolvedLanguage || "tr")
                                                    .format("ll")}
                                            </DescriptionContainer.Item>
                                        </DescriptionContainer>
                                    </>
                                )}
                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                orderData.data.delivery.orders &&
                                orderData.data.delivery.orders.some(order => order.callback) && (
                                    <Collapse style={{ marginBottom: 16 }}>
                                        <Collapse.Panel 
                                            header={t("fields.orderInformations", {ns: "common"})} 
                                            key="orderInformations"
                                        >
                                            <CountdownButton
                                                countdown={5}
                                                onClick={triggerCallback}
                                            />
                                            <Table
                                                className="callback-orders-table"
                                                bordered
                                                rowKey="_id"
                                                dataSource={orderData.data.delivery.orders.filter(
                                                    order => order.callback
                                                )}
                                                pagination={false}
                                                data-testid="collectingPointTable"
                                                expandable={{
                                                    expandedRowRender: record => (
                                                        <Table
                                                            bordered
                                                            pagination={false}
                                                            dataSource={record.orderInformations}
                                                        >
                                                            <Column
                                                                dataIndex="meta"
                                                                title={t("fields.status", {ns: "common"})}
                                                                render={text => <span data-testid="name">{text.state}</span>}
                                                            />
                                                            <Column
                                                                dataIndex="meta"
                                                                title={t("fields.explanation", {ns: "common"})}
                                                                render={text => {
                                                                    const statusObj = mapDeliveryStatuses.find(status => status.value === text.state);
                                                                    console.log("statusObj", statusObj);
                                                                    return <span data-testid="name">{statusObj?.name}</span>;
                                                                }}
                                                            />
                                                            <Column
                                                                dataIndex="isSent"
                                                                title={t("fields.isSentInfo", {ns: "common"})}
                                                                render={text => (
                                                                    <span data-testid="name">
                                                                        {text ? "İletildi" : "İletilmedi"}
                                                                    </span>
                                                                )}
                                                            />
                                                            <Column
                                                                dataIndex="retryCount"
                                                                title={t("fields.retryCount", {ns: "common"})}
                                                                render={text => <span data-testid="name">{text}</span>}
                                                            />
                                                        </Table>
                                                    )
                                                }}
                                            >
                                                <Column
                                                    dataIndex="orderNumber"
                                                    title={t("fields.orderNumber", {ns: "common"})}
                                                    render={text => <span data-testid="name">{text}</span>}
                                                />
                                                <Column
                                                    title={t("fields.informationCount", {ns: "common"})}
                                                    render={(_, {orderInformations}) => (
                                                        <span data-testid="name">{orderInformations.length}</span>
                                                    )}
                                                />
                                                <Column
                                                    title={t("fields.sentInformationCount", {ns: "common"})}
                                                    render={(_, {orderInformations}) => (
                                                        <span data-testid="name">
                                                            {orderInformations.filter(info => info.isSent).length}
                                                        </span>
                                                    )}
                                                />
                                            </Table>
                                        </Collapse.Panel>
                                    </Collapse>
                                )}
                        </Col>
                        <Col span={10}>
                            {orderData.data?.delivery.customType == "slot" && (
                                <SlotDetailCard orderData={orderData}></SlotDetailCard>
                            )}
                            {/*@TODO: split this component  */}
                            <StepContainer
                                data-testid="orderDetailCard"
                                data-testvalue={
                                    orderData.data?.steps?.slice(-1)[0]?.endDate && delivered
                                        ? orderData.data.delivery.currentStepIndex
                                        : orderData.data.delivery.currentStepIndex - 1
                                }
                            >
                                <TableTitle>
                                    {deliveryType === "productReturn"
                                        ? t("returnDetail", {ns: "orderDetail"})
                                        : t("orderDetail", {ns: "orderDetail"})}
                                    {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                        !delivered &&
                                        !canceled &&
                                        !returned &&
                                        (process.env.REACT_APP_ENV === "development" ||
                                            process.env.REACT_APP_ENV === "staging" ||
                                            stores.meStore.role == "admin" ||
                                            stores.meStore.role === "superadmin") && (
                                            <RightButton
                                                disabled={loading}
                                                onClick={updateStep}
                                                data-testid="nextStepButton"
                                                icon={
                                                    loading ? (
                                                        <LoadingOutlined />
                                                    ) : (
                                                        <FastForwardOutlined />
                                                    )
                                                }
                                            >
                                                {loading
                                                    ? t("loading", {ns: "orderDetail"})
                                                    : t("actions.nextStep", {ns: "orderDetail"})}
                                            </RightButton>
                                        )}
                                </TableTitle>
                                <NextStepModal
                                    isOpenNextStepModal={isOpenNextStepModal}
                                    setIsOpenNextStepModal={setIsOpenNextStepModal}
                                    nextStepData={nextStepData?.data}
                                />
                                {loading && (
                                    <SpinnerContainer>
                                        <Spin spinning={loading} />
                                    </SpinnerContainer>
                                )}
                                <OrderSteps
                                    orderData={orderData}
                                    transferData={transferData}
                                    renderProofIcon={renderProofIcon}
                                />
                                {renderMergedOrderStats()}
                                <OrderTotalTime>
                                    {t("fields.elapsedTime", {ns: "common"})}:{" "}
                                    <span data-testid="deliveredTime" />
                                    {delivered
                                        ? calculateElapsedTime(totalTime, deliveredTime)
                                        : orderData?.data?.delivery?.merged ||
                                          orderData?.data?.delivery?.summary?.types?.some(
                                              type => type === "autoCanceled"
                                          )
                                        ? calculateElapsedTime(
                                              orderData.data.delivery.createdAt,
                                              orderData.data.delivery.cancelDate
                                          )
                                        : sortedSteps[sortedSteps.length - 1]?.endDate
                                        ? calculateElapsedTime(
                                              orderData.data.delivery.createdAt,
                                              sortedSteps[sortedSteps.length - 1]?.endDate
                                          )
                                        : canceled
                                        ? t("canceled.shortName", {ns: "deliveryStatuses"})
                                        : calculateElapsedTime(totalTime, new Date())}
                                </OrderTotalTime>
                            </StepContainer>
                            {transferData?.data?.length > 0 && (
                                <TransferInfoContainer data-testid="transferInfoCard">
                                    <TableTitle>
                                        {t("transferHistory", {ns: "orderDetail"})}
                                    </TableTitle>
                                    <TransferLogList
                                        itemLayout="vertical"
                                        split={false}
                                        loading={transferLoading}
                                        dataSource={transferData.data}
                                        renderItem={(item, index) => (
                                            <>
                                                <List.Item
                                                    data-testvalue={index}
                                                    data-testid="from"
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Avatar
                                                                size={50}
                                                                data-testid="courierPhoto"
                                                                src={
                                                                    item.fromCourier?.photo
                                                                        ? `${config.getBasePublicUrl()}api/panel/v1/image/${
                                                                              item.fromCourier
                                                                                  ?.photo
                                                                          }`
                                                                        : placeholder
                                                                }
                                                            />
                                                        }
                                                        title={
                                                            <a
                                                                data-testid="courierName"
                                                                href={`${config.getBasePublicUrl()}couriers/${
                                                                    item.fromCourier._id
                                                                }`}
                                                            >
                                                                {item.fromCourier?.fullname}
                                                            </a>
                                                        }
                                                        description={
                                                            <>
                                                                <Row data-testid="description">
                                                                    {index === 0 ? (
                                                                        <TransferDescription>
                                                                            <Text type="success">
                                                                                {t("fields.start", {
                                                                                    ns: "common"
                                                                                })}
                                                                                :{" "}
                                                                            </Text>
                                                                            <span data-testid="startTime">
                                                                                {moment(
                                                                                    orderData.data
                                                                                        ?.delivery
                                                                                        .createdAt
                                                                                ).format(
                                                                                    "HH:mm:ss"
                                                                                )}
                                                                            </span>
                                                                        </TransferDescription>
                                                                    ) : (
                                                                        <TransferDescription>
                                                                            <Text type="danger">
                                                                                {" "}
                                                                                Transfer:{" "}
                                                                            </Text>
                                                                            <span data-testid="transferTime">
                                                                                {moment(
                                                                                    item?.createdAt
                                                                                ).format(
                                                                                    "HH:mm:ss"
                                                                                )}
                                                                            </span>
                                                                        </TransferDescription>
                                                                    )}
                                                                </Row>
                                                            </>
                                                        }
                                                    />
                                                </List.Item>
                                                {index === transferData.data.length - 1 && (
                                                    <List.Item
                                                        data-testvalue={index + 1}
                                                        data-testid="to"
                                                    >
                                                        <List.Item.Meta
                                                            avatar={
                                                                <Avatar
                                                                    data-testid="courierPhoto"
                                                                    size={50}
                                                                    src={
                                                                        item.toCourier?.photo
                                                                            ? `${config.getBasePublicUrl()}api/panel/v1/image/${
                                                                                  item.toCourier
                                                                                      ?.photo
                                                                              }`
                                                                            : placeholder
                                                                    }
                                                                />
                                                            }
                                                            title={
                                                                <a
                                                                    data-testid="courierName"
                                                                    href={`${config.getBasePublicUrl()}couriers/${
                                                                        item.toCourier._id
                                                                    }`}
                                                                >
                                                                    {item.toCourier?.fullname}
                                                                </a>
                                                            }
                                                            description={
                                                                <TransferDescription>
                                                                    <Text type="danger">
                                                                        {" "}
                                                                        Transfer:{" "}
                                                                    </Text>
                                                                    <span data-testid="transferTime">
                                                                        {moment(
                                                                            item?.createdAt
                                                                        ).format("HH:mm:ss")}
                                                                    </span>
                                                                </TransferDescription>
                                                            }
                                                        />
                                                    </List.Item>
                                                )}
                                            </>
                                        )}
                                    />
                                </TransferInfoContainer>
                            )}
                        </Col>
                    </Row>
                    <Row justify="end" data-testid="actionButtons">
                        {!delivered &&
                            !canceled &&
                            !returned &&
                            (["admin", "oUser", "superadmin"].includes(stores.meStore.role) ||
                                stores.meStore.subcompanies.some(
                                    subcompany => subcompany.subCompanyWorkType === "dedicated"
                                )) && (
                                <Row>
                                    <LeftButton
                                        data-testid="splitButton"
                                        type="primary"
                                        onClick={() => {
                                            setHideOrderError(true);
                                            setModalVisible(!modalVisible);
                                            setModalType("splitOrder");
                                            setModalData({deliveryId, orderSteps});
                                        }}
                                    >
                                        {t("actions.splitOrder", {ns: "orderDetail"})}
                                    </LeftButton>
                                </Row>
                            )}
                        {stores.meStore.checkRole("transferOrder") &&
                            !delivered &&
                            !canceled &&
                            !returned && (
                                <Row>
                                    <MiddleButton
                                        type="primary"
                                        data-testid="transferButton"
                                        onClick={() => {
                                            setModalVisible(!modalVisible);
                                            setModalType("transfer");
                                        }}
                                        icon={<CloseCircleOutlined />}
                                    >
                                        {t("actions.transferOrder", {ns: "orderDetail"})}
                                    </MiddleButton>
                                </Row>
                            )}

                        {stores.meStore.checkRole("cancelRepeatOrder") &&
                            !delivered &&
                            !canceled &&
                            !returned && (
                                <Row>
                                    <RightButton
                                        type="danger"
                                        data-testid="cancelButton"
                                        onClick={() => {
                                            setModalVisible(!modalVisible);
                                            setModalType("cancel");
                                            setModalData({
                                                deliveryId,
                                                orderId: text._id
                                            });
                                        }}
                                        icon={<CloseCircleOutlined />}
                                    >
                                        {t("actions.cancelOrder", {ns: "common"})}
                                    </RightButton>
                                </Row>
                            )}
                        {stores.meStore.checkRole("cancelRepeatOrder") &&
                            (delivered || canceled || returned) && (
                                <Row>
                                    <RightButton
                                        type="primary"
                                        data-testid="repeatButton"
                                        onClick={() => {
                                            history.push("/third-party-request/new/" + deliveryId);
                                        }}
                                        icon={<UndoOutlined />}
                                    >
                                        {t("actions.resendOrder", {ns: "orderDetail"})}
                                    </RightButton>
                                </Row>
                            )}
                    </Row>
                    <OrderNoteModal
                        visible={orderNoteModalVisible}
                        onClose={() => setOrderNoteModalVisible(false)}
                        onOk={handleOrderNoteSave}
                        order={selectedOrder}
                        operationNotes={operationNotes || []}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};
